<script>
import DataLayer from '@services/data-layer/index'

// `genre` is declared here rather than in setup because vue-meta evaluates page() through a
// watcher that cannot see <script setup> bindings, and the genre is fetched async so page()
// cannot re-derive it from $route or $store. Setup writes it once the fetch lands.
export default {
    data() {
        return {
            genre: null,
        }
    },

    page() {
        const title = this.genre?.name ? `${this.genre.name} Mixes` : 'Genre Mixes'
        const description = this.genre?.name
            ? `${this.genre.name} mixes from global underground artists`
            : 'genre mixes from global underground artists'
        const image = `${import.meta.env.VITE_CLIENT_URL}/frisky-social-share-image.png`
        const canonicalUrl = `https://frisky.fm${this.$route.path}`
        return {
            title,
            link: [{ rel: 'canonical', vmid: 'canonical', href: canonicalUrl }],
            meta: [
                { name: 'title', vmid: 'title', content: title },
                { name: 'description', vmid: 'description', content: description },

                { property: 'og:title', vmid: 'og:title', content: title },
                { property: 'og:description', vmid: 'og:description', content: description },
                { property: 'og:image', vmid: 'og:image', content: image },
                { property: 'og:type', vmid: 'og:type', content: 'website' },
                { property: 'og:url', vmid: 'og:url', content: canonicalUrl },
                { property: 'og:site_name', vmid: 'og:site_name', content: 'FRISKY' },

                { name: 'twitter:card', vmid: 'twitter:card', content: 'summary_large_image' },
                { name: 'twitter:title', vmid: 'twitter:title', content: title },
                { name: 'twitter:description', vmid: 'twitter:description', content: description },
                { name: 'twitter:image', vmid: 'twitter:image', content: image },
                { name: 'twitter:url', vmid: 'twitter:url', content: canonicalUrl },
            ],
        }
    },
}
</script>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router/composables'
import SharedCarousel from '@components/carousel/shared-carousel'
import SharedCarouselSlide from '@components/carousel/shared-carousel-slide'
import Layout from '@layouts/main-layout'
import MixGridBlock from '@components/mix-grid-block'
import MixGridTile from '@components/mix-grid-tile'
import MixTableBlock from '@components/mix-table-block.v2'
import ArtistBlock from '@components/artist/artist-block'
import GenreHero from './genre-hero'

const POPULAR_PAGE_SIZE = 5
const LATEST_PAGE_SIZE = 12
const VISIBLE_TRENDING_MIXES = 6

const instance = getCurrentInstance()
const route = useRoute()

// The genre is declared as options data above because page() cannot read a <script setup>
// binding, and is written from here once it arrives — by onMounted, `$data` exists. Setup keeps
// its own copy under a different name: a binding called `genre` would shadow the data property
// in the template.
const loadedGenre = ref(null)

const allPopularMixes = ref(Array.from(Array(5), (_mix, id) => ({ id, genre: [] })))
const latestMixes = ref(Array.from(Array(10), (_mix, id) => ({ id, genre: [] })))
const artists = ref(Array.from(Array(10), (_artist, id) => ({ id })))
const trendingMixes = ref([])

const visiblePopularMixes = ref(POPULAR_PAGE_SIZE)
const visibleLatestMixes = ref(LATEST_PAGE_SIZE)

// Shrinks when a page comes back empty, so it is state rather than a constant despite the name
// it carried before.
const latestPageSize = ref(LATEST_PAGE_SIZE)

const genreError = ref(null)
const contentError = ref(null)

const errorMessage = computed(() => genreError.value || contentError.value)

const popularMixes = computed(() => allPopularMixes.value.slice(0, visiblePopularMixes.value))

const trendingTitle = computed(() => `Trending in ${loadedGenre.value?.name || ''}`.trim())

const displayedTrendingMixes = computed(() =>
    trendingMixes.value.slice(0, VISIBLE_TRENDING_MIXES)
)

function showMorePopularMixes() {
    visiblePopularMixes.value *= 2
}

function showMoreLatestMixes() {
    DataLayer.mixes
        .getLatestByGenre(loadedGenre.value.name, latestPageSize.value, visibleLatestMixes.value)
        .then(response => {
            latestMixes.value.push(...response.body)
            if (response.body.length === 0) {
                latestPageSize.value -= 1
            }
        })
    visibleLatestMixes.value += latestPageSize.value
}

function loadGenreContent(loaded) {
    return Promise.all([
        DataLayer.mixes.getPopularByGenre(loaded.name),
        DataLayer.mixes.getLatestByGenre(loaded.name),
        DataLayer.artists.getRandomByGenre(loaded.name),
        DataLayer.mixes.getTrendingByGenre(loaded.slug),
    ])
        .then(([popular, latest, randomArtists, trending]) => {
            allPopularMixes.value = popular.body
            latestMixes.value = latest.body
            artists.value = randomArtists.body
            trendingMixes.value = trending.body
        })
        .catch(() => {
            contentError.value = 'Unable to load genre content. Please try again later.'
        })
}

onMounted(async () => {
    try {
        const res = await DataLayer.genres.getBySlug({ slug: route.params.slug })

        loadedGenre.value = res.body
        // page() reads this off the instance; `$data` is initialised by the time onMounted runs.
        instance.proxy.genre = res.body

        loadGenreContent(res.body)
    } catch (error) {
        genreError.value = 'Unable to load genre. Please try again later.'
    }
})
</script>

<template>
    <Layout>
        <GenreHero
            :text="genre?.name || ''"
            :resource="genre"
            class="hero"
        />
        <BaseContent>
            <BaseSection v-if="errorMessage">
                <div class="error-message">{{ errorMessage }}</div>
            </BaseSection>
            <BaseSection
                v-if="displayedTrendingMixes.length"
                :title="trendingTitle"
                padded
            >
                <SharedCarousel
                    :per-page-custom="[[0, 2], [768, 3], [1025, 6]]"
                    :pagination-enabled="false"
                    :scroll-per-page="false"
                    :min-swipe-distance="100"
                    :space-padding="0"
                    :mouse-drag="displayedTrendingMixes.length > 1"
                    class="carousel"
                >
                    <SharedCarouselSlide
                        v-for="(mix, index) in displayedTrendingMixes"
                        :key="`genre-trending-${mix.id}-${index}`"
                        class="slide"
                    >
                        <MixGridTile
                            :mix="mix"
                            :index="index"
                        />
                    </SharedCarouselSlide>
                </SharedCarousel>
            </BaseSection>
            <BaseSection
                v-if="popularMixes && popularMixes.length > 0"
                title="Popular Mixes"
                short
                padded
            >
                <MixTableBlock
                    :mixes="popularMixes"
                    :buttons="{ playCount: true }"
                    genres
                    is-indexed-list
                />
                <BaseLoadMore
                    :id="genre?.name"
                    :list="popularMixes"
                    :visible="5"
                    :more="5"
                    :max="10"
                    :get-fc="showMorePopularMixes"
                    :update-fc="() => {}"
                />
            </BaseSection>
            <BaseSection
                v-if="latestMixes && latestMixes.length > 0"
                title="Mixes"
                padded
            >
                <MixGridBlock :mixes="latestMixes" />
                <BaseLoadMore
                    :id="genre?.name"
                    :list="latestMixes"
                    :visible="12"
                    :more="12"
                    :get-fc="showMoreLatestMixes"
                    :update-fc="() => {}"
                />
            </BaseSection>
            <BaseSection
                v-if="artists && artists.length > 0"
                title="Artists"
            >
                <ArtistBlock
                    :artists="artists"
                    wrapping
                    padded-left-mobile
                />
            </BaseSection>
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@import '@design';

.hero {
    margin-bottom: $s-xxl;
}

.carousel {
    position: relative;
    margin: 0 (-$size-grid-padding);
    overflow: visible;
}

.slide {
    position: relative;
    padding: 0 $size-grid-padding;

    &:hover,
    &:focus-within {
        z-index: 2;
    }
}

:deep(.carousel .shared-carousel-viewport),
:deep(.carousel .swiper-wrapper),
:deep(.carousel .shared-carousel-slide) {
    overflow: visible;
}

.error-message {
    padding: $s-l;
    margin-bottom: $s-l;
    color: $c-white;
    text-align: center;
    background-color: rgba($c-red, 0.2);
    border: 1px solid $c-red;
    border-radius: 4px;
}
</style>
